import {
  action,
  observable,
  runInAction,
} from 'mobx';
import RootStore from './root-store';
import logger from '../utils/logger';
import type {
  SlumpTable,
  SlumpTableSteps,
} from '../../types/api/SlumpTable';
import { toNumber } from '../utils/numbers';

export const MAX_SLUMP_CHANGE = 5;
export const MIN_SLUMP_CHANGE = -4;

class SlumpTableStore {
  rootStore: RootStore;

  @observable
    slumpTables: Array<SlumpTable> = [];
  @observable
    selectedSlumpTableId: number|null = null;
  @observable
    isLoading: boolean = false;
  @observable
    error: any = null;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get locationId() {
    return this.rootStore.locationsStore.locationId;
  }

  get waterUnit() {
    return this.rootStore.ticketStore.getWaterUnit();
  }

  setSlumpTableId(id: number|null): void {
    this.selectedSlumpTableId = id;
  };

  @action
    fetchSlumpTables = async () => {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.error = null;
      const url = `/api/location/${this.locationId}/slumpTable`;
      try {
        await this.api.storeDB.init();
        const localData = await this.api.storeDB.getByKey(url);

        runInAction(() => {
          if (localData) {
            this.slumpTables = localData;
          }
        });

        const serverData: any = await this.api.makeAuthorizedRequest(url);

        runInAction(() => {
          this.slumpTables = serverData;
        });
      } catch (error) {
        logger.error(error);
        runInAction(() => {
          this.error = error;
        });
      } finally {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    };

  generateSlumpSteps(min: number = MIN_SLUMP_CHANGE, max: number = MAX_SLUMP_CHANGE): SlumpTableSteps[] {
    return Array.from({ length: max - min + 1 }, (_, idx) => {
      const change = min + idx;
      const adjustment = change === 0 ? 0 : change > 0 ? 100 : -100;

      return { change, adjustment };
    });
  };

  getSlumpTableById(id: number): SlumpTable|undefined {
    return this.slumpTables.find(v => v.id === id);
  };

  @action
    addSlumpTable = async (slumpTable: SlumpTable): Promise<void> => {
      await this.api.makeAuthorizedRequest(`/api/location/${this.locationId}/slumpTable`, slumpTable, 'POST');
    };

  @action
    updateSlumpTable = async (slumpTable: SlumpTable): Promise<void> => {
      await this.api.makeAuthorizedRequest(`/api/location/${this.locationId}/slumpTable/${slumpTable.id}`, slumpTable, 'PUT');
    };

  @action
    removeSlumpTable = async (id: number): Promise<void> => {
      await this.api.makeAuthorizedRequest(`/api/location/${this.locationId}/slumpTable/${id}`, {}, 'DELETE');
    };

  calculateCumulativeChange(change: number, steps: Array<SlumpTableSteps>): number {
    if (!change || !steps?.length) {
      return 0;
    }

    let cumulativeChange = 0;
    if (change > 0) {
      cumulativeChange += steps
        .filter(v => v.change > 0 && v.change <= change)
        .map(v => Number(v.adjustment))
        .reduce((a, b) => a + b, 0);
    } else if (change < 0) {
      cumulativeChange += steps
        .filter(v => v.change < 0 && v.change >= change)
        .map(v => Number(v.adjustment))
        .reduce((a, b) => a + b, 0);
    }

    return toNumber(cumulativeChange/100, 2);
  }
}

export default SlumpTableStore;
